import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { BottomSheet, Box, Divider, Popover, TextButton, useDevice } from 'seagull';
import { Edit } from 'seagull/icons/content-edit/Edit';
import { PlusCircle } from 'seagull/icons/essentials/PlusCircle';
import { useIoContext } from '~/contexts';
import { useUpdateInvoiceDashboardPreferences } from '~/hooks/reactQuery/mutations/useUpdateInvoiceDashboardPreferences';
import { useInvoiceDashboardPreferences } from '~/hooks/reactQuery/queries/useInvoiceDashboardPreferences';
import { useInvoiceMetrics } from '~/hooks/reactQuery/queries/useInvoiceMetrics';
import { useLocaleKeys } from '~/i18n';
import { DEFAULT_INVOICE_DASHBOARD_CARDS } from '~/types/api.model';
import { AddWidgetMenu } from './AddWidgetMenu/AddWidgetMenu';
import { DonutRingCard } from './DonutRingCard/DonutRingCard';
import styles from './InvoiceMetricsSection.module.scss';
import { CARD_LOCALE_KEY, currentFiltersMatchActiveCard, filterForAgingSegment, filterForAnchorSegment, filterForCard, filterForOutstandingSegment, formatCurrency, isNumberOnlyStatusBucket, isSparklineStatusBucket, subtractCardFilter } from './InvoiceMetricsSection.utils';
import { MetricsCardStrip } from './MetricsCardStrip/MetricsCardStrip';
import { NumberOnlyCard } from './NumberOnlyCard/NumberOnlyCard';
import { SegmentedBarCard } from './SegmentedBarCard/SegmentedBarCard';
import { SparklineCard } from './SparklineCard/SparklineCard';
import { getDateRangeForTimePeriod, TimePeriodSelector } from './TimePeriodSelector/TimePeriodSelector';
const computeTrendPercent = (current, previous)=>{
    if (current == null || previous == null || previous === 0) return null;
    return (current - previous) / previous * 100;
};
export const InvoiceMetricsSection = (param)=>{
    let { currentFilters, period: controlledPeriod, onPeriodChange, onApplyFilter } = param;
    var _ref;
    const { getNow } = useIoContext();
    const { t } = useLocaleKeys('components.invoiceMetricsSection');
    const { isMobileDevice } = useDevice();
    const [internalPeriod, setInternalPeriod] = useState({
        preset: 'last-1-month'
    });
    const period = controlledPeriod !== null && controlledPeriod !== void 0 ? controlledPeriod : internalPeriod;
    const handlePeriodChange = onPeriodChange !== null && onPeriodChange !== void 0 ? onPeriodChange : setInternalPeriod;
    const [isEditMode, setIsEditMode] = useState(false);
    const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
    const [activeCardId, setActiveCardId] = useState(null);
    const [activeSegmentId, setActiveSegmentId] = useState(null);
    const [draftCards, setDraftCards] = useState(null);
    const addWidgetButtonRef = useRef(null);
    const { preferences } = useInvoiceDashboardPreferences();
    const { save: savePreferences } = useUpdateInvoiceDashboardPreferences();
    const savedCardIds = (_ref = preferences === null || preferences === void 0 ? void 0 : preferences.cards) !== null && _ref !== void 0 ? _ref : DEFAULT_INVOICE_DASHBOARD_CARDS;
    const visibleCardIds = isEditMode && draftCards != null ? draftCards : savedCardIds;
    const dateRange = useMemo(()=>getDateRangeForTimePeriod(period, getNow()), [
        period,
        getNow
    ]);
    const [debouncedSearch, setDebouncedSearch] = useState(currentFilters.search);
    useEffect(()=>{
        const handle = setTimeout(()=>setDebouncedSearch(currentFilters.search), 300);
        return ()=>clearTimeout(handle);
    }, [
        currentFilters.search
    ]);
    const segmentDerivedFilter = useMemo(()=>{
        if (activeCardId == null || activeSegmentId == null) return null;
        if (activeCardId === 'outstanding') return filterForOutstandingSegment(activeSegmentId);
        if (activeCardId === 'aging-breakdown') return filterForAgingSegment(activeSegmentId, getNow());
        if (activeCardId === 'anchor-vs-external') return filterForAnchorSegment(activeSegmentId);
        return null;
    }, [
        activeCardId,
        activeSegmentId,
        getNow
    ]);
    const metricsFilters = useMemo(()=>{
        const subtracted = subtractCardFilter(currentFilters, segmentDerivedFilter);
        return {
            issuedOnSince: dateRange.issuedOnSince != null ? new Date(dateRange.issuedOnSince) : null,
            issuedOnUntil: dateRange.issuedOnUntil != null ? new Date(dateRange.issuedOnUntil) : null,
            displayStatusV2: subtracted.displayStatusV2,
            paymentDetails: subtracted.paymentDetails,
            search: debouncedSearch,
            dueDateSince: subtracted.dueDateSince != null ? new Date(subtracted.dueDateSince) : null,
            dueDateUntil: subtracted.dueDateUntil != null ? new Date(subtracted.dueDateUntil) : null,
            qboIntegration: currentFilters.qboIntegration,
            xeroIntegration: currentFilters.xeroIntegration
        };
    }, [
        dateRange.issuedOnSince,
        dateRange.issuedOnUntil,
        currentFilters,
        segmentDerivedFilter,
        debouncedSearch
    ]);
    const { metrics, isLoading, isFetching } = useInvoiceMetrics(metricsFilters, {
        enabled: visibleCardIds.length > 0
    });
    const isRefetching = isFetching && !isLoading && metrics != null;
    const { displayStatusV2: currentStatuses, paymentDetails: currentPaymentDetails, dueDateSince: currentDueDateSince, dueDateUntil: currentDueDateUntil } = currentFilters;
    useEffect(()=>{
        if (activeCardId == null) return;
        if (!currentFiltersMatchActiveCard(activeCardId, activeSegmentId, {
            displayStatusV2: currentStatuses,
            paymentDetails: currentPaymentDetails,
            dueDateSince: currentDueDateSince,
            dueDateUntil: currentDueDateUntil
        }, getNow())) {
            setActiveCardId(null);
            setActiveSegmentId(null);
        }
    }, [
        currentStatuses,
        currentPaymentDetails,
        currentDueDateSince,
        currentDueDateUntil,
        activeCardId,
        activeSegmentId,
        getNow
    ]);
    const toggleActive = (id)=>{
        const isCurrentlyActiveCard = activeCardId === id && activeSegmentId == null;
        if (isCurrentlyActiveCard) {
            setActiveCardId(null);
            setActiveSegmentId(null);
            onApplyFilter === null || onApplyFilter === void 0 ? void 0 : onApplyFilter({
                displayStatusV2: []
            });
        } else {
            setActiveCardId(id);
            setActiveSegmentId(null);
            onApplyFilter === null || onApplyFilter === void 0 ? void 0 : onApplyFilter(filterForCard(id));
        }
    };
    const handleSegmentClick = (cardId, segmentId, filter)=>{
        if (filter == null) return;
        const isCurrentlyActiveSegment = activeCardId === cardId && activeSegmentId === segmentId;
        if (isCurrentlyActiveSegment) {
            setActiveCardId(null);
            setActiveSegmentId(null);
            onApplyFilter === null || onApplyFilter === void 0 ? void 0 : onApplyFilter({
                displayStatusV2: []
            });
        } else {
            setActiveCardId(cardId);
            setActiveSegmentId(segmentId);
            onApplyFilter === null || onApplyFilter === void 0 ? void 0 : onApplyFilter(filter);
        }
    };
    const renderCard = (id, params)=>{
        var _ref, _ref1;
        const isActive = activeCardId === id;
        const onClick = ()=>toggleActive(id);
        const sharedProps = {
            isEditMode: params.isEditMode,
            dragHandle: params.dragHandle,
            onRemove: params.onRemove,
            isActive,
            isLoading,
            isRefetching,
            onClick
        };
        const dataMissing = metrics == null;
        const segmentActiveOverride = (segments)=>{
            if (!isActive || activeSegmentId == null) return null;
            const segment = segments.find((s)=>s.id === activeSegmentId);
            if (segment == null) return null;
            const subtitleLookup = t.segmentsActiveSubtitle[segment.id];
            if (subtitleLookup == null) return null;
            return {
                primaryValue: formatCurrency(segment.amount),
                subtitle: subtitleLookup()
            };
        };
        if (id === 'collection-rate') {
            const c = t.cards.collectionRate;
            const ratio = metrics != null && metrics.collectionRate.totalAmount > 0 ? metrics.collectionRate.paidAmount / metrics.collectionRate.totalAmount : 0;
            return /*#__PURE__*/ _jsx(DonutRingCard, _object_spread_props(_object_spread({}, sharedProps), {
                title: c.title(),
                tooltip: c.tooltip(),
                ratio: ratio,
                isEmpty: dataMissing || metrics.collectionRate.totalAmount === 0,
                subtitle: c.subtitle(),
                emptyPrimary: c.emptyPrimary(),
                emptySubtitle: c.emptySubtitle()
            }));
        }
        if (id === 'avg-days-to-pay') {
            var _ref2, _ref3, _ref4;
            const c = t.cards.avgDaysToPay;
            const avg = (_ref2 = metrics === null || metrics === void 0 ? void 0 : metrics.avgDaysToPay.average) !== null && _ref2 !== void 0 ? _ref2 : null;
            const trend = computeTrendPercent(avg, (_ref3 = metrics === null || metrics === void 0 ? void 0 : metrics.avgDaysToPay.previousPeriodAverage) !== null && _ref3 !== void 0 ? _ref3 : null);
            return /*#__PURE__*/ _jsx(SparklineCard, _object_spread_props(_object_spread({}, sharedProps), {
                title: c.title(),
                tooltip: c.tooltip(),
                primaryValue: avg != null ? c.primaryValue({
                    days: Math.round(avg)
                }) : '',
                sparkline: (_ref4 = metrics === null || metrics === void 0 ? void 0 : metrics.avgDaysToPay.sparkline) !== null && _ref4 !== void 0 ? _ref4 : [],
                trendPercent: trend,
                fallbackSubtitle: c.subtitleFallback(),
                isEmpty: dataMissing || avg == null,
                emptyPrimary: c.emptyPrimary(),
                emptySubtitle: c.emptySubtitle()
            }));
        }
        if (id === 'anchor-vs-external') {
            var _ref5, _ref6, _ref7, _ref8;
            const c = t.cards.anchorVsExternal;
            const { onClick: _ } = sharedProps, rest = _object_without_properties(sharedProps, [
                "onClick"
            ]);
            const anchorPaid = (_ref5 = metrics === null || metrics === void 0 ? void 0 : metrics.anchorVsExternal.anchorPaidAmount) !== null && _ref5 !== void 0 ? _ref5 : 0;
            const externalPaid = (_ref6 = metrics === null || metrics === void 0 ? void 0 : metrics.anchorVsExternal.externalPaidAmount) !== null && _ref6 !== void 0 ? _ref6 : 0;
            const totalPaid = anchorPaid + externalPaid;
            const anchorShare = totalPaid > 0 ? Math.round(anchorPaid / totalPaid * 100) : 0;
            const externalShare = totalPaid > 0 ? Math.round(externalPaid / totalPaid * 100) : 0;
            const segments = [
                {
                    id: 'anchorPaid',
                    label: t.segments.anchorPaid(),
                    amount: anchorPaid,
                    color: '#7C5DFA',
                    isActive: isActive && activeSegmentId === 'anchorPaid',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'anchorPaid', filterForAnchorSegment('anchorPaid')) : undefined
                },
                {
                    id: 'externalPaid',
                    label: t.segments.externalPaid(),
                    amount: externalPaid,
                    color: '#C7CDD9',
                    isActive: isActive && activeSegmentId === 'externalPaid',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'externalPaid', filterForAnchorSegment('externalPaid')) : undefined
                }
            ];
            const override = segmentActiveOverride(segments);
            return /*#__PURE__*/ _jsx(SegmentedBarCard, _object_spread_props(_object_spread({}, rest), {
                onClickCard: onClick,
                title: c.title(),
                tooltip: c.tooltip(),
                primaryValue: (_ref7 = override === null || override === void 0 ? void 0 : override.primaryValue) !== null && _ref7 !== void 0 ? _ref7 : c.primaryValue({
                    share: anchorShare
                }),
                subtitle: (_ref8 = override === null || override === void 0 ? void 0 : override.subtitle) !== null && _ref8 !== void 0 ? _ref8 : c.subtitle({
                    externalShare
                }),
                segments: segments,
                isEmpty: dataMissing || totalPaid === 0,
                emptyPrimary: c.emptyPrimary(),
                emptySubtitle: c.emptySubtitle()
            }));
        }
        if (id === 'outstanding') {
            var _ref9, _ref10, _ref11, _ref12, _ref13, _ref14, _ref15, _ref16, _ref17, _ref18;
            const c = t.cards.outstanding;
            const { onClick: _ } = sharedProps, rest = _object_without_properties(sharedProps, [
                "onClick"
            ]);
            const o = metrics === null || metrics === void 0 ? void 0 : metrics.outstanding;
            const total = ((_ref9 = o === null || o === void 0 ? void 0 : o.issued) !== null && _ref9 !== void 0 ? _ref9 : 0) + ((_ref10 = o === null || o === void 0 ? void 0 : o.processing) !== null && _ref10 !== void 0 ? _ref10 : 0) + ((_ref11 = o === null || o === void 0 ? void 0 : o.pending) !== null && _ref11 !== void 0 ? _ref11 : 0) + ((_ref12 = o === null || o === void 0 ? void 0 : o.overdue) !== null && _ref12 !== void 0 ? _ref12 : 0);
            const segments = [
                {
                    id: 'issued',
                    label: t.segments.issued(),
                    amount: (_ref13 = o === null || o === void 0 ? void 0 : o.issued) !== null && _ref13 !== void 0 ? _ref13 : 0,
                    color: '#7C5DFA',
                    isActive: isActive && activeSegmentId === 'issued',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'issued', filterForOutstandingSegment('issued')) : undefined
                },
                {
                    id: 'processing',
                    label: t.segments.processing(),
                    amount: (_ref14 = o === null || o === void 0 ? void 0 : o.processing) !== null && _ref14 !== void 0 ? _ref14 : 0,
                    color: '#9F89F8',
                    isActive: isActive && activeSegmentId === 'processing',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'processing', filterForOutstandingSegment('processing')) : undefined
                },
                {
                    id: 'pending',
                    label: t.segments.pending(),
                    amount: (_ref15 = o === null || o === void 0 ? void 0 : o.pending) !== null && _ref15 !== void 0 ? _ref15 : 0,
                    color: '#FFB547',
                    isActive: isActive && activeSegmentId === 'pending',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'pending', filterForOutstandingSegment('pending')) : undefined
                },
                {
                    id: 'overdue',
                    label: t.segments.overdue(),
                    amount: (_ref16 = o === null || o === void 0 ? void 0 : o.overdue) !== null && _ref16 !== void 0 ? _ref16 : 0,
                    color: '#F44337',
                    isActive: isActive && activeSegmentId === 'overdue',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'overdue', filterForOutstandingSegment('overdue')) : undefined
                }
            ];
            const override = segmentActiveOverride(segments);
            return /*#__PURE__*/ _jsx(SegmentedBarCard, _object_spread_props(_object_spread({}, rest), {
                onClickCard: onClick,
                title: c.title(),
                tooltip: c.tooltip(),
                primaryValue: (_ref17 = override === null || override === void 0 ? void 0 : override.primaryValue) !== null && _ref17 !== void 0 ? _ref17 : formatCurrency(total),
                subtitle: (_ref18 = override === null || override === void 0 ? void 0 : override.subtitle) !== null && _ref18 !== void 0 ? _ref18 : c.subtitle(),
                segments: segments,
                isEmpty: dataMissing || total === 0,
                emptyPrimary: c.emptyPrimary(),
                emptySubtitle: c.emptySubtitle()
            }));
        }
        if (id === 'aging-breakdown') {
            var _ref19, _ref20, _ref21, _ref22, _ref23, _ref24, _ref25, _ref26, _ref27, _ref28, _ref29, _ref30;
            const c = t.cards.agingBreakdown;
            const { onClick: _ } = sharedProps, rest = _object_without_properties(sharedProps, [
                "onClick"
            ]);
            const a = metrics === null || metrics === void 0 ? void 0 : metrics.aging;
            const total = ((_ref19 = a === null || a === void 0 ? void 0 : a.current) !== null && _ref19 !== void 0 ? _ref19 : 0) + ((_ref20 = a === null || a === void 0 ? void 0 : a.days1to30) !== null && _ref20 !== void 0 ? _ref20 : 0) + ((_ref21 = a === null || a === void 0 ? void 0 : a.days31to60) !== null && _ref21 !== void 0 ? _ref21 : 0) + ((_ref22 = a === null || a === void 0 ? void 0 : a.days61to90) !== null && _ref22 !== void 0 ? _ref22 : 0) + ((_ref23 = a === null || a === void 0 ? void 0 : a.days90plus) !== null && _ref23 !== void 0 ? _ref23 : 0);
            const segments = [
                {
                    id: 'current',
                    label: t.segments.current(),
                    amount: (_ref24 = a === null || a === void 0 ? void 0 : a.current) !== null && _ref24 !== void 0 ? _ref24 : 0,
                    color: '#28C474',
                    isActive: isActive && activeSegmentId === 'current',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'current', filterForAgingSegment('current', getNow())) : undefined
                },
                {
                    id: 'days1to30',
                    label: t.segments.days1to30(),
                    amount: (_ref25 = a === null || a === void 0 ? void 0 : a.days1to30) !== null && _ref25 !== void 0 ? _ref25 : 0,
                    color: '#FFB547',
                    isActive: isActive && activeSegmentId === 'days1to30',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'days1to30', filterForAgingSegment('days1to30', getNow())) : undefined
                },
                {
                    id: 'days31to60',
                    label: t.segments.days31to60(),
                    amount: (_ref26 = a === null || a === void 0 ? void 0 : a.days31to60) !== null && _ref26 !== void 0 ? _ref26 : 0,
                    color: '#FF8A47',
                    isActive: isActive && activeSegmentId === 'days31to60',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'days31to60', filterForAgingSegment('days31to60', getNow())) : undefined
                },
                {
                    id: 'days61to90',
                    label: t.segments.days61to90(),
                    amount: (_ref27 = a === null || a === void 0 ? void 0 : a.days61to90) !== null && _ref27 !== void 0 ? _ref27 : 0,
                    color: '#F44337',
                    isActive: isActive && activeSegmentId === 'days61to90',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'days61to90', filterForAgingSegment('days61to90', getNow())) : undefined
                },
                {
                    id: 'days90plus',
                    label: t.segments.days90plus(),
                    amount: (_ref28 = a === null || a === void 0 ? void 0 : a.days90plus) !== null && _ref28 !== void 0 ? _ref28 : 0,
                    color: '#B61212',
                    isActive: isActive && activeSegmentId === 'days90plus',
                    onClick: onApplyFilter != null ? ()=>handleSegmentClick(id, 'days90plus', filterForAgingSegment('days90plus', getNow())) : undefined
                }
            ];
            const override = segmentActiveOverride(segments);
            return /*#__PURE__*/ _jsx(SegmentedBarCard, _object_spread_props(_object_spread({}, rest), {
                onClickCard: onClick,
                title: c.title(),
                tooltip: c.tooltip(),
                primaryValue: (_ref29 = override === null || override === void 0 ? void 0 : override.primaryValue) !== null && _ref29 !== void 0 ? _ref29 : formatCurrency(total),
                subtitle: (_ref30 = override === null || override === void 0 ? void 0 : override.subtitle) !== null && _ref30 !== void 0 ? _ref30 : c.subtitle(),
                segments: segments,
                isEmpty: dataMissing || total === 0,
                emptyPrimary: c.emptyPrimary(),
                emptySubtitle: c.emptySubtitle()
            }));
        }
        if (id === 'avg-invoice') {
            var _ref31, _ref32, _ref33, _ref34;
            const c = t.cards.avgInvoice;
            const avg = (_ref31 = metrics === null || metrics === void 0 ? void 0 : metrics.avgInvoice.average) !== null && _ref31 !== void 0 ? _ref31 : null;
            const count = (_ref32 = metrics === null || metrics === void 0 ? void 0 : metrics.avgInvoice.count) !== null && _ref32 !== void 0 ? _ref32 : 0;
            const trend = computeTrendPercent(avg, (_ref33 = metrics === null || metrics === void 0 ? void 0 : metrics.avgInvoice.previousPeriodAverage) !== null && _ref33 !== void 0 ? _ref33 : null);
            return /*#__PURE__*/ _jsx(SparklineCard, _object_spread_props(_object_spread({}, sharedProps), {
                title: c.title(),
                tooltip: c.tooltip(),
                primaryValue: avg != null ? formatCurrency(avg) : '',
                sparkline: (_ref34 = metrics === null || metrics === void 0 ? void 0 : metrics.avgInvoice.sparkline) !== null && _ref34 !== void 0 ? _ref34 : [],
                trendPercent: trend,
                fallbackSubtitle: c.subtitleFallback({
                    count
                }),
                isEmpty: dataMissing || avg == null,
                emptyPrimary: c.emptyPrimary(),
                emptySubtitle: c.emptySubtitle()
            }));
        }
        if (isSparklineStatusBucket(id)) {
            var _ref35, _ref36, _ref37, _ref38;
            const bucket = metrics != null ? metrics[id] : null;
            const localeKey = CARD_LOCALE_KEY[id];
            const c = t.cards[localeKey];
            const trend = computeTrendPercent((_ref35 = bucket === null || bucket === void 0 ? void 0 : bucket.amount) !== null && _ref35 !== void 0 ? _ref35 : null, (_ref36 = bucket === null || bucket === void 0 ? void 0 : bucket.previousPeriodAmount) !== null && _ref36 !== void 0 ? _ref36 : null);
            const hasInvoices = bucket != null && bucket.count > 0;
            return /*#__PURE__*/ _jsx(SparklineCard, _object_spread_props(_object_spread({}, sharedProps), {
                title: c.title(),
                tooltip: c.tooltip(),
                primaryValue: hasInvoices ? formatCurrency(bucket.amount) : '',
                sparkline: (_ref37 = bucket === null || bucket === void 0 ? void 0 : bucket.sparkline) !== null && _ref37 !== void 0 ? _ref37 : [],
                trendPercent: trend,
                fallbackSubtitle: t.invoicesCount({
                    count: (_ref38 = bucket === null || bucket === void 0 ? void 0 : bucket.count) !== null && _ref38 !== void 0 ? _ref38 : 0
                }),
                isEmpty: dataMissing || !hasInvoices,
                emptyPrimary: c.emptyPrimary(),
                emptySubtitle: c.emptySubtitle()
            }));
        }
        const bucketData = isNumberOnlyStatusBucket(id) && metrics != null ? metrics[id] : null;
        const amount = (_ref = bucketData === null || bucketData === void 0 ? void 0 : bucketData.amount) !== null && _ref !== void 0 ? _ref : 0;
        const count = (_ref1 = bucketData === null || bucketData === void 0 ? void 0 : bucketData.count) !== null && _ref1 !== void 0 ? _ref1 : 0;
        const localeKey = CARD_LOCALE_KEY[id];
        const c = t.cards[localeKey];
        return /*#__PURE__*/ _jsx(NumberOnlyCard, _object_spread_props(_object_spread({}, sharedProps), {
            title: c.title(),
            tooltip: c.tooltip(),
            amount: amount,
            count: count,
            emptyPrimary: c.emptyPrimary(),
            emptySubtitle: c.emptySubtitle()
        }));
    };
    const items = useMemo(()=>{
        return visibleCardIds.map((id)=>({
                id,
                render: (params)=>renderCard(id, params)
            }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        visibleCardIds,
        t,
        isEditMode,
        activeCardId,
        activeSegmentId,
        onApplyFilter,
        metrics,
        isLoading,
        isRefetching
    ]);
    const arraysEqual = (a, b)=>a.length === b.length && a.every((value, index)=>value === b[index]);
    const handleReorder = (newOrderIds)=>{
        setDraftCards(newOrderIds);
    };
    const handleRemove = (id)=>{
        setDraftCards(visibleCardIds.filter((existing)=>existing !== id));
    };
    const handleAdd = (id)=>{
        if (!visibleCardIds.includes(id)) {
            setDraftCards([
                ...visibleCardIds,
                id
            ]);
        }
        setIsAddWidgetOpen(false);
    };
    const handleEnterEditMode = ()=>{
        if (preferences == null) return;
        setDraftCards(preferences.cards);
        setIsEditMode(true);
    };
    const handleDone = ()=>{
        if (draftCards != null && preferences != null && !arraysEqual(draftCards, preferences.cards)) {
            savePreferences({
                cards: draftCards,
                updatedAt: preferences.updatedAt
            });
        }
        setIsEditMode(false);
        setIsAddWidgetOpen(false);
        setDraftCards(null);
    };
    return /*#__PURE__*/ _jsxs(Box, {
        className: cn(styles.root, {
            mobile: isMobileDevice
        }),
        testId: "invoice-metrics-section",
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                className: styles.controls,
                children: [
                    /*#__PURE__*/ _jsx(TimePeriodSelector, {
                        value: period,
                        onChange: handlePeriodChange,
                        now: getNow()
                    }),
                    isEditMode ? /*#__PURE__*/ _jsxs(Box, {
                        className: styles.editControls,
                        children: [
                            /*#__PURE__*/ _jsx(TextButton, {
                                ref: addWidgetButtonRef,
                                prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {
                                    size: 20
                                }),
                                underline: "none",
                                weight: "normal",
                                onClick: ()=>setIsAddWidgetOpen((shown)=>!shown),
                                testId: "invoice-metrics-section-add-widget",
                                children: t.addWidgetButton()
                            }),
                            /*#__PURE__*/ _jsx(Divider, {
                                orientation: "vertical",
                                className: styles.editDivider,
                                margin: 8
                            }),
                            /*#__PURE__*/ _jsx(TextButton, {
                                prefixIcon: /*#__PURE__*/ _jsx(Edit, {
                                    size: 20
                                }),
                                underline: "none",
                                weight: "normal",
                                onClick: handleDone,
                                testId: "invoice-metrics-section-done",
                                children: t.doneButton()
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(Box, {
                        className: styles.customizeButton,
                        children: /*#__PURE__*/ _jsx(TextButton, {
                            prefixIcon: /*#__PURE__*/ _jsx(Edit, {
                                size: 20
                            }),
                            underline: "none",
                            weight: "normal",
                            disabled: preferences == null,
                            onClick: handleEnterEditMode,
                            testId: "invoice-metrics-section-customize-toggle",
                            children: t.customize()
                        })
                    })
                ]
            }),
            visibleCardIds.length > 0 && /*#__PURE__*/ _jsx(MetricsCardStrip, {
                items: items,
                isEditMode: isEditMode,
                onReorder: handleReorder,
                onRemove: handleRemove,
                testId: "invoice-metrics-section-strip"
            }),
            isMobileDevice ? /*#__PURE__*/ _jsx(BottomSheet, {
                open: isAddWidgetOpen,
                onClose: ()=>setIsAddWidgetOpen(false),
                paddingBottom: 0,
                children: /*#__PURE__*/ _jsx(AddWidgetMenu, {
                    visibleCardIds: visibleCardIds,
                    onAddCard: handleAdd,
                    testId: "invoice-metrics-section-add-widget-menu"
                })
            }) : /*#__PURE__*/ _jsx(Popover, {
                open: isAddWidgetOpen,
                anchorEl: addWidgetButtonRef.current,
                onClose: ()=>setIsAddWidgetOpen(false),
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right'
                },
                transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                },
                children: /*#__PURE__*/ _jsx(AddWidgetMenu, {
                    visibleCardIds: visibleCardIds,
                    onAddCard: handleAdd,
                    testId: "invoice-metrics-section-add-widget-menu"
                })
            })
        ]
    });
};
